import type {
  EditorialCommentary,
  EditorialCommentaryUpsertRequest,
  EditorialContentDetail,
  EditorialContentUpdateRequest,
} from "@/api/researchCms.service";
import type { OrgFormValues } from "../schemas/orgViewSchemas";
import { EMPTY_TRANSLATIONS_FORM } from "./translationsForm";

// The API SAR scores response carries metadata (id, timestamps, audit columns) alongside
// the score values. The mapper filters by value type when hydrating the form.
export type RecordedSarsScores = Record<string, unknown>;

type DetailClient = EditorialContentDetail["clients"][number];
type DetailContact = EditorialContentDetail["contacts"][number];
type DetailMatter = EditorialContentDetail["matters"][number];

type ContactEntry = { name: string; role: DetailContact["role"] };

const byPosition = <T extends { position: number }>(rows: T[]): T[] =>
  [...rows].sort((left, right) => left.position - right.position);

// The parser leaves stray whitespace on names, so a value the user tidied would otherwise read as
// a rename and lose the row's flags. Only the match key is trimmed; the payload keeps the form's value.
const matchKey = (text: string | null): string => (text ?? "").trim();

// Rows sharing a key queue up so each one hands its id to a different form row, and a form row
// beyond the queue is genuinely new — no loaded id can be claimed twice.
function indexByKey<T>(rows: T[], key: (row: T) => string): Map<string, T[]> {
  const index = new Map<string, T[]>();
  for (const row of rows) {
    const rowKey = key(row);
    const queued = index.get(rowKey);
    if (queued) queued.push(row);
    else index.set(rowKey, [row]);
  }
  return index;
}

function claim<T>(index: Map<string, T[]>, key: string): T | undefined {
  const queued = index.get(key);
  if (!queued) return undefined;
  const claimed = queued.shift();
  if (queued.length === 0) index.delete(key);
  return claimed;
}

export const EMPTY_ORG_FORM: OrgFormValues = {
  practiceHeads: [],
  otherKeyLawyers: [],
  keyClients: [],
  confidentialClients: [],
  matterSummaries: [],
  publishableMatters: [],
  confidentialMatters: [],
  sarScores: {},
  postContent: "",
  clerksCommentary: "",
  teamSizePartners: null,
  teamSizeLawyers: null,
  editorCommentBox: "",
  researcherNotes: "",
  rankings: {},
  translations: EMPTY_TRANSLATIONS_FORM,
};

// The API types every child's text as a non-null string, but the columns behind them are
// nullable, so each one is coalesced on the way into the form rather than trusted.
export function toOrgFormValues(
  detail: EditorialContentDetail,
  commentary: EditorialCommentary | null,
  sars?: RecordedSarsScores | null,
): OrgFormValues {
  const contacts = byPosition(detail.contacts);
  const matters = byPosition(detail.matters);
  const sarScores: Record<string, number | null> = {};
  // The API response carries metadata (id, timestamps) alongside scores, so only numeric
  // values and null are copied. A recorded 0 is a real worst score, not an absence, so
  // each key is only overwritten when the researcher has actually scored it.
  if (sars) {
    for (const [key, value] of Object.entries(sars)) {
      if (typeof value === "number" || value === null) sarScores[key] = value;
    }
  }
  return {
    practiceHeads: contacts
      .filter(contact => contact.role === "head")
      .map(contact => contact.name ?? ""),
    otherKeyLawyers: contacts
      .filter(contact => contact.role === "partner")
      .map(contact => contact.name ?? ""),
    // A client's kind comes from its publishable flag: publishable clients are the key clients,
    // and the rest are confidential.
    keyClients: byPosition(detail.clients)
      .filter(client => client.publishable)
      .map(client => client.name ?? ""),
    confidentialClients: byPosition(detail.clients)
      .filter(client => !client.publishable)
      .map(client => client.name ?? ""),
    // A matter's kind comes from its two flags: matterSummary marks a summary row, and among the
    // rest publishable separates the publishable matters from the confidential ones.
    matterSummaries: matters
      .filter(matter => matter.matterSummary)
      .map(matter => ({ text: matter.summary ?? "" })),
    publishableMatters: matters
      .filter(matter => !matter.matterSummary && matter.publishable)
      .map(matter => ({ text: matter.summary ?? "" })),
    confidentialMatters: matters
      .filter(matter => !matter.matterSummary && !matter.publishable)
      .map(matter => ({ text: matter.summary ?? "" })),
    sarScores,
    ...toCommentaryFormValues(commentary),
    teamSizePartners: detail.teamSizePartners ?? null,
    teamSizeLawyers: detail.teamSizeLawyers ?? null,
    rankings: {},
    translations: EMPTY_ORG_FORM.translations,
  };
}

// Commentary lives against the edition BPA, so a firm with no content row can still carry it.
export function toCommentaryFormValues(
  commentary: EditorialCommentary | null,
): Pick<
  OrgFormValues,
  "postContent" | "clerksCommentary" | "editorCommentBox" | "researcherNotes"
> {
  return {
    postContent: commentary?.postContent ?? "",
    clerksCommentary: commentary?.clerksCommentary ?? "",
    editorCommentBox: commentary?.editorCommentBox ?? "",
    researcherNotes: commentary?.researcherNotes ?? "",
  };
}

// A 409 on create adopts a row another researcher may already have filled in, while the local
// form was never hydrated from it. PATCH writes explicit nulls and replaces child collections
// wholesale, so any field the form left empty must carry the adopted row's value or the save
// silently wipes it; a field the researcher typed still wins. Against a freshly created row
// every adopted field is empty, so the merge is a no-op there. SAR scores and the commentary
// text fields save through their own legs and are deliberately left alone.
export function mergeAdoptedValues(
  values: OrgFormValues,
  adopted: EditorialContentDetail,
): OrgFormValues {
  const adoptedValues = toOrgFormValues(adopted, null);
  return {
    ...values,
    teamSizePartners: values.teamSizePartners ?? adoptedValues.teamSizePartners,
    teamSizeLawyers: values.teamSizeLawyers ?? adoptedValues.teamSizeLawyers,
    practiceHeads:
      values.practiceHeads.length > 0 ? values.practiceHeads : adoptedValues.practiceHeads,
    otherKeyLawyers:
      values.otherKeyLawyers.length > 0 ? values.otherKeyLawyers : adoptedValues.otherKeyLawyers,
    keyClients: values.keyClients.length > 0 ? values.keyClients : adoptedValues.keyClients,
    confidentialClients:
      values.confidentialClients.length > 0
        ? values.confidentialClients
        : adoptedValues.confidentialClients,
    matterSummaries:
      values.matterSummaries.length > 0 ? values.matterSummaries : adoptedValues.matterSummaries,
    publishableMatters:
      values.publishableMatters.length > 0
        ? values.publishableMatters
        : adoptedValues.publishableMatters,
    confidentialMatters:
      values.confidentialMatters.length > 0
        ? values.confidentialMatters
        : adoptedValues.confidentialMatters,
  };
}

// PATCH replaces each child collection wholesale. The form now carries every published flag it
// round-trips via a row's group — a client's publishable, a matter's publishable/matterSummary —
// so those are set from the group rather than the row. Only newClient, which the form never shows,
// is still carried over from the loaded row matched by name, or it would be wiped.
export function toUpdateRequest(
  values: OrgFormValues,
  detail: EditorialContentDetail,
): EditorialContentUpdateRequest {
  // Indexed in position order, the same order toOrgFormValues built the form rows in, so queued
  // ids reach the positions they were loaded against.
  const clientsByName: Map<string, DetailClient[]> = indexByKey(
    byPosition(detail.clients),
    client => matchKey(client.name),
  );
  const contactsByName: Map<string, DetailContact[]> = indexByKey(
    byPosition(detail.contacts),
    contact => matchKey(contact.name),
  );
  const mattersBySummary: Map<string, DetailMatter[]> = indexByKey(
    byPosition(detail.matters),
    matter => matchKey(matter.summary),
  );

  const contactEntries: ContactEntry[] = [
    ...values.practiceHeads.map((name): ContactEntry => ({ name, role: "head" })),
    ...values.otherKeyLawyers.map((name): ContactEntry => ({ name, role: "partner" })),
  ];

  // The two client groups collapse back into a single ordered list — publishable first, then
  // confidential — each row tagged with the flag its group stands for.
  const clientEntries = [
    ...values.keyClients.map(name => ({ name, publishable: true })),
    ...values.confidentialClients.map(name => ({ name, publishable: false })),
  ];

  // The three form groups collapse back into a single ordered list — summaries first, then
  // publishable, then confidential — each row tagged with the flags its group stands for.
  const matterEntries = [
    ...values.matterSummaries.map(matter => ({
      text: matter.text,
      publishable: true,
      matterSummary: true,
    })),
    ...values.publishableMatters.map(matter => ({
      text: matter.text,
      publishable: true,
      matterSummary: false,
    })),
    ...values.confidentialMatters.map(matter => ({
      text: matter.text,
      publishable: false,
      matterSummary: false,
    })),
  ];

  return {
    teamSizePartners: values.teamSizePartners,
    teamSizeLawyers: values.teamSizeLawyers,
    clients: clientEntries.map((client, index) => {
      const existing = claim(clientsByName, matchKey(client.name));
      return {
        ...(existing ? { id: existing.id } : {}),
        name: client.name,
        position: index + 1,
        publishable: client.publishable,
        newClient: existing?.newClient ?? null,
      };
    }),
    contacts: contactEntries.map((entry, index) => {
      const existing = claim(contactsByName, matchKey(entry.name));
      return {
        ...(existing ? { id: existing.id } : {}),
        name: entry.name,
        role: entry.role,
        position: index + 1,
        publishable: existing?.publishable ?? true,
      };
    }),
    matters: matterEntries.map((matter, index) => {
      const existing = claim(mattersBySummary, matchKey(matter.text));
      return {
        ...(existing ? { id: existing.id } : {}),
        summary: matter.text,
        position: index + 1,
        publishable: matter.publishable,
        matterSummary: matter.matterSummary,
      };
    }),
  };
}

/**
 * The language a commentary row is keyed by: the write-up's own, else the publication's input
 * language. Undefined when neither is known, so the API falls back to the publication itself.
 */
export function commentaryLanguage(
  writeUp: { languageIso: string } | null | undefined,
  publication: { inputLanguage: string } | null | undefined,
): string | undefined {
  return writeUp?.languageIso ?? publication?.inputLanguage;
}

const emptyToNull = (value: string): string | null => (value.trim() === "" ? null : value);

export function toCommentaryRequest(
  values: OrgFormValues,
): Omit<EditorialCommentaryUpsertRequest, "companyId" | "contactId" | "languageIso"> {
  return {
    postContent: values.postContent,
    clerksCommentary: emptyToNull(values.clerksCommentary),
    editorCommentBox: emptyToNull(values.editorCommentBox),
    researcherNotes: emptyToNull(values.researcherNotes),
  };
}

// Gates row creation: an all-empty editorial section must not mint a commentary row on save.
export function hasCommentaryContent(values: OrgFormValues): boolean {
  return [
    values.postContent,
    values.clerksCommentary,
    values.editorCommentBox,
    values.researcherNotes,
  ].some(value => value.trim() !== "");
}

// Gates parent-row creation on a first save: commentary stands alone, so it must not mint a content row.
export function hasSubmissionContent(values: OrgFormValues): boolean {
  const anyText = (items: string[]) => items.some(item => item.trim() !== "");
  const anyMatter = (items: OrgFormValues["matterSummaries"]) =>
    items.some(matter => matter.text.trim() !== "");
  return (
    anyMatter(values.matterSummaries) ||
    anyMatter(values.publishableMatters) ||
    anyMatter(values.confidentialMatters) ||
    anyText(values.practiceHeads) ||
    anyText(values.otherKeyLawyers) ||
    anyText(values.keyClients) ||
    anyText(values.confidentialClients) ||
    values.teamSizePartners !== null ||
    values.teamSizeLawyers !== null ||
    Object.values(values.sarScores).some(score => score !== null) ||
    Object.values(values.rankings ?? {}).some(r => r.ranked || r.tier !== null)
  );
}
